import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { STATUS } from "@/lib/checkins";

const Kpi = ({ label, value, testId, hint }) => (
  <Card className="p-4" data-testid={testId}>
    <div className="text-2xs font-semibold uppercase tracking-[0.2em] text-zinc-500">{label}</div>
    <div className="mt-1 font-mono-num text-2xl font-medium text-zinc-950">{value ?? "—"}</div>
    {hint && <div className="text-xs text-zinc-500">{hint}</div>}
  </Card>
);

export function ReportsPanel() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/checkin-reports", { params: { days: 90 } }).then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <Loader2 className="h-5 w-5 animate-spin text-zinc-400" />;
  const enrolled = Object.values(d.enrolments).reduce((a, b) => a + b, 0);
  return (
    <div className="space-y-5" data-testid="checkin-reports">
      <p className="text-xs text-zinc-500">Last {d.window_days} days. Monitoring metrics only — they don&apos;t show that a program caused any change.</p>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Kpi label="Enrolled" value={enrolled} hint={`${d.enrolments.active || 0} active · ${d.enrolments.opted_out || 0} opted out`} testId="report-enrolled" />
        <Kpi label="Due next 7 days" value={d.due_next_7_days} testId="report-due" />
        <Kpi label="Invitations sent" value={d.invitations_sent} hint={`${d.delivery_failures} failed · ${d.reminders_sent} reminded`} testId="report-sent" />
        <Kpi label="Completed ≤7 days" value={d.completion_rate_7d != null ? `${d.completion_rate_7d}%` : null} hint="target ≥70%" testId="report-completion" />
        <Kpi label="Submissions" value={d.submissions} testId="report-submissions" />
        <Kpi label="Median time" value={d.median_completion_minutes != null ? `${d.median_completion_minutes} min` : null} hint="target ≤4 min" testId="report-median" />
        <Kpi label="Open alerts" value={d.open_alerts} hint={Object.entries(d.alerts_by_severity).map(([k, v]) => `${v} ${k}`).join(" · ")} testId="report-alerts" />
        <Kpi label="Useful summaries" value={d.useful_pct != null ? `${d.useful_pct}%` : null} hint={`${d.ratings} ratings · target ≥80% · ${d.overrides} overrides`} testId="report-useful" />
      </div>
      <Card className="p-4">
        <h3 className="font-display text-base font-semibold">Domain status distribution</h3>
        <table className="mt-2 w-full text-sm" data-testid="report-distribution">
          <thead><tr className="text-left text-2xs uppercase tracking-[0.15em] text-zinc-500"><th className="py-1">Domain</th>{Object.keys(STATUS).map((k) => <th key={k}>{STATUS[k].label}</th>)}</tr></thead>
          <tbody className="divide-y divide-zinc-100">
            {Object.entries(d.domain_distribution).map(([dom, c]) => (
              <tr key={dom}><td className="py-1.5">{dom}</td>{Object.keys(STATUS).map((k) => <td key={k} className="font-mono-num">{c[k] || 0}</td>)}</tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
